<script>
import { useQueryParameter } from '../../../../Composables/useQueryParameter.ts'
import useUser from '../../../../Composables/User/useUser.ts'
import { Role } from '../../../../Constants/Role.ts'
import ContractSignatureTab from '../../../../Views/ContractSignatureTab.vue'
import mixinComponent from '../../Mixins/mixinComponent'
import { globalVariablesMixin } from '../../Mixins/global'
import FormCreator from '../UI/Form/FormCreator.vue'
import BlockContent from '../UI/Content/BlockContent.vue'
import FieldCreator from '../UI/Form/FieldCreator.vue'
import ButtonAll from '../UI/Button/ButtonAll.vue'
import TabMenu from '../UI/TabMenu.vue'
import DocumentList from '../Document/DocumentList.vue'
import ContractSignatureAlert from './ContractSignatureAlert.vue'
import ContractSignatureBlock from './ContractSignatureBlock.vue'
import { useModalMessage } from '../../../../Composables/useModalMessage.ts'
import { ContractSignatureError } from '../../../../Errors/ContractSignatureError.js'
import WalletContractInvoiceList from '../Wallet/WalletContractInvoiceList.vue'
import ModalConfirmation from '../UI/Modal/ModalConfirmation.vue'

export default {
  components: {
    BlockContent,
    ButtonAll,
    ContractSignatureAlert,
    ContractSignatureBlock,
    ContractSignatureTab,
    DocumentList,
    FieldCreator,
    FormCreator,
    ModalConfirmation,
    TabMenu,
    WalletContractInvoiceList,
  },
  mixins: [mixinComponent, globalVariablesMixin],

  props: {
    contractId: { type: String, required: true },
    contractNumber: { type: String, required: true },
    contract: { type: Object, required: true },
    def: { type: Object, required: true },
  },

  data: () => ({
    tab: null,
    rib: false,
    loading: false,
    sending: false,
    loadField: false,
    signedOK: false,
    canceling: false,
    documents: [],
    configEquipment: [],
    configError: [],
    formData: null,
    openedDocumentSignId: null,
  }),

  setup() {
    const { hasRole } = useUser()
    const { showSuccess } = useModalMessage()
    const { value: currentTabKey } = useQueryParameter('tab', 'attachments')

    return { hasRole, showSuccess, currentTabKey }
  },

  watch: {
    tab: {
      handler(tab) {
        this.currentTabKey.value = tab.key
      },
    },
  },

  beforeMount() {
    this.getJSON('equipments/def?mainId=' + this.contract.equipment.id).then((json) => {
      this.configEquipment = json.config
    })

    this.tab = this.tabs.find(({ key }) => key === this.currentTabKey)
  },

  computed: {
    Role() {
      return Role
    },
    tabs() {
      return [
        {
          key: 'attachments',
          show: true,
          title: this.$t('contract.document.tab.attachments'),
        },
        {
          key: 'signatureFlows',
          show: this.hasRole(Role.ENHANCED_SIGN),
          title: this.$t('contract.document.tab.signatureFlows'),
        },
        {
          key: 'supplierInvoices',
          show: this.hasRole(Role.INVOICE_SUPPLIER_SHOW),
          title: this.$t('contract.document.tab.supplierInvoices'),
        },
      ]
    },
    ribok() {
      return (
        (this.rib && this.contract.rib) || this.contract.finance.paymentMean.id !== 1 || !this.contract.ribMandatory
      )
    },
    isContractValidate() {
      return ['VALIDATED', 'PENDING', 'STUDY', 'STUDIED'].includes(this.contract.status)
    },
    isEsign() {
      const validStatus = ['CREATED', 'VALIDATED', 'STUDY', 'STUDIED', 'PENDING']
      return validStatus.includes(this.contract.status)
    },
    isPending() {
      const pendingStatus = [
        'STUDIED',
        'PENDING',
        'CLIENT_SIGNED',
        'WAITING',
        'DELIVERED',
        'COMPLETED',
        'COMPLETE',
        'FINISH',
      ]
      return pendingStatus.includes(this.contract.status)
    },
    isDelivery() {
      const deliveryStatus = ['WAITING', 'DELIVERED', 'COMPLETED', 'COMPLETE', 'FINISH']
      return deliveryStatus.includes(this.contract.status)
    },
    contractDocument() {
      return this.documents.find(({ document }) => document.documentType.internalName === 'contract')
    },
    signComments() {
      return this.contractDocument?.document.documentSignFilesStatuses
        .map(({ documentSigner }) => documentSigner.comment)
        .filter(Boolean)
    },
    delivered() {
      return ['DELIVERED', 'COMPLETED', 'COMPLETE', 'FINISH'].includes(this.contract.status)
    },
    canBeSignedByLessor() {
      return (
        conf.signContractByLessor === true && !user.isReadOnly && this.delivered && !!this.lessorContractDocumentSign
      )
    },
    lessorContractDocumentSign() {
      return this.contractDocument?.document.documentSignFilesStatuses.find(
        ({ documentSigner }) => documentSigner.signType === 'companySigner',
      )
    },
    isBuyerSmsSignMode() {
      if (this.contract.documentTemplate === null) {
        return false
      }

      return this.contract.documentTemplate.isBuyerSignModeSms
    },
    validateEquipment() {
      let validate = true
      for (const field in this.configEquipment) {
        const config = this.configEquipment[field]
        if (config?.options?.required) {
          if (!this.contract.equipment[field]) {
            this.configError.push({ id: this.contract.equipment.id, field: field })
            validate = false
          }
          if (this.contract.equipment.equipments.length > 0) {
            for (const equipment of this.contract.equipment.equipments) {
              if (!equipment[field]) {
                this.configError.push({ id: equipment.id, field: field })
                validate = false
              }
            }
          }
        }
      }

      return validate
    },
    validateBuyerSigner() {
      return !this.isBuyerSmsSignMode || this.contract.buyer?.phone !== null
    },
    signError() {
      if (!this.contractDocument || this.contractDocument.document.documentSignFilesStatuses.length === 0) {
        return null
      }

      const hasSignatureCanceled = this.contractDocument.document.documentSignFilesStatuses.some(
        ({ status }) => status === 'CANCELLED',
      )
      if (hasSignatureCanceled) {
        return new ContractSignatureError(this.$t('esign.cancelled'), 'esign.cancelled')
      }

      const hasSignatureInError = this.contractDocument.document.documentSignFilesStatuses.some(
        ({ status }) => status === 'ERROR',
      )
      if (hasSignatureInError) {
        return new ContractSignatureError(this.$t('esign.error'), 'esign.error')
      }

      const isSigned = this.contractDocument.document.documentSignFilesStatuses.every(({ status }) =>
        ['SIGNED', 'SIGNED_FULL'].includes(status),
      )
      if (isSigned) {
        return new ContractSignatureError(this.$t('esign.alreadySigned'), 'esign.alreadySigned')
      }

      return null
    },
    getSignUrl() {
      return `signature-contrat.php?i=${this.contractId}&n=${this.contractNumber}`
    },
    getSignEdition() {
      return `signature-contrat-edition.php?i=${this.contractId}`
    },
    isValidationRequired() {
      if (this.hasValidator && !this.contractDocument) {
        return true
      }

      return this.contractDocument?.document.documentSignFilesStatuses.some(
        ({ documentSigner }) => documentSigner.validator && !['SIGNED', 'SIGNED_FULL'].includes(documentSigner.status),
      )
    },
    hasValidator() {
      if (this.contract.documentTemplate === null) {
        return false
      }

      return this.contract.documentTemplate.hasValidator
    },
    isDocumentCanceled() {
      if (!this.contractDocument) {
        return false
      }

      return this.contractDocument?.document.documentSignFiles.some(({ documentSign }) =>
        ['CANCELLED'].includes(documentSign.status),
      )
    },
    getSignLabel() {
      return this.isValidationRequired ? 'contract.document.validateNow' : 'contract.document.signNow'
    },
    getValidationInfoLabel() {
      if (this.isValidationRequired) {
        return this.$t('contract.document.validation.created')
      }

      if (this.isDocumentCanceled) {
        return this.$t('contract.document.validation.canceled')
      }

      return this.$t('contract.document.validation.success')
    },
    getValidationClasses() {
      return {
        'text-center': true,
        'font-weight-bold': true,
        'text-danger': this.isDocumentCanceled,
      }
    },
  },

  methods: {
    updateData(newData) {
      this.formData = newData
    },
    async cancelSign() {
      this.canceling = true
      try {
        await this.patchJSON(`contracts/${this.contractId}/cancelSign`, {})
        location.reload()
      } finally {
        this.canceling = false
      }
    },
    documentLoaded(info) {
      this.documents = info.documents

      // on regarde si le rib a été chargé.
      this.rib = false
      for (const [key, doc] of Object.entries(info.documents)) {
        if (doc.document.documentType.internalName === 'rib' && doc.document.status !== 'REFUSED') {
          this.setValue(this.contract, 'rib', true)
          this.rib = true
        }
      }

      // Si il ne reste plus aucun document à chargé, alors on rafraichit la page pour mettre à jour le statut du contrat.
      if (info.missings.length === 0 && info.refresh) {
        location.reload()
      }
    },
    generateEquipmentErrorMessages() {
      let errors = []
      const groupedErrors = this.configError.reduce((acc, error) => {
        const { field, id } = error
        if (!acc[field]) {
          acc[field] = []
        }
        acc[field].push(id)
        return acc
      }, {})

      for (const field in groupedErrors) {
        const ids = groupedErrors[field].join(', ')
        errors.push(this.$t('equipment.action.required', { field: this.$t(`equipment.fields.${field}.label`), ids }))
      }

      this.generateMessageError(this.$t('main.error'), errors)
    },
    generateSignerErrorMessage() {
      this.generateMessageError(this.$t('contract.fields.buyer.phone.required', { data: this.contract.buyer.name }))
    },
    generateMessageError(messageError = null, messageErrors = []) {
      this.$root.jsonStatus = 'error'
      this.$root.jsonStatusText = messageError
      if (messageErrors.length > 0) {
        this.$root.errorsInjected = messageErrors
      }

      $('#modal-waiting-text').modal({ backdrop: 'static', keyboard: false })
    },
    checkSignVerified() {
      if (false === this.validateEquipment) {
        this.generateEquipmentErrorMessages()
        return false
      }

      if (false === this.validateBuyerSigner) {
        this.generateSignerErrorMessage()
        return false
      }

      return true
    },
    async sendEmail() {
      if (this.checkSignVerified()) {
        this.loading = true
        await this.getJSON(`contracts/${this.contractId}/send`)
          .then((json) => {
            this.showSuccess(this.$t('contract.document.sendSign.signTo', { name: `${json?.name}` }))
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    sendSign() {
      if (this.checkSignVerified()) {
        window.open(this.getSignUrl, '_blank')
      }
    },
    setBuild(bool) {
      this.loadField = bool
    },
    isSending(bool) {
      this.sending = bool
    },
    patchSignedAt() {
      this.signedOK = true
      this.$refs.formcontract.methods()
    },
    refreshDocument(json) {
      if (this.signedOK) {
        this.reloadPage()
      } else {
        this.$refs.documentList.getDocuments()
        this.contract = json
      }
    },
    async download() {
      await this.downloadFile(`contracts/${this.contractId}/download`, `Contrat-${this.contractNumber}.pdf`)
    },
    reloadPage() {
      location.reload()
    },
    contractContainsActorType(type) {
      return this.contract.finance.actors.some((actor) => actor.type === type)
    },
    async sendContractSigningEmailToLessor() {
      await this.patchJSON(
        `documentsigns/${this.contractDocument.id}/send`,
        {
          documentSignerId: this.lessorContractDocumentSign.documentSigner.id,
          token: this.lessorContractDocumentSign.documentSigner.token,
        },
        {},
      )
      this.toast(this.$t('contract.document.signing.emailSent'))
    },
    onCancelSignature() {
      this.$refs.cancelSignatureConfirm.showModal()
    },
    async onCancelSignatureConfirmation() {
      this.$refs.cancelSignatureConfirm.closeModal()
      await this.cancelSign()
    },
  },
}
</script>

<template>
  <div id="contract-document">
    <div v-if="Object.keys(def).length > 0 && Object.keys(contract).length > 0">
      <FormCreator
        ref="formcontract"
        endpoint="contracts"
        method="patch"
        :id="Number(contractId)"
        :config-def="def"
        :config-object="contract"
        dynamic
        class-to-add="d-none"
        @form-build="setBuild"
        @form-return="refreshDocument"
        @update-data="updateData"
      />

      <TabMenu v-model="tab" :tabs="tabs" />

      <div v-if="tab?.key === 'attachments'">
        <BlockContent v-if="def.language" e2e-block="contractDocumentLanguage">
          <div class="row">
            <h4 class="col-md-6">{{ $t('contract.information.language') }}</h4>
            <div class="col-md-6">
              <FieldCreator
                :form-data="formData"
                name="language"
                form-ref="formcontract"
                domain="contract"
                type-value="string"
                refresh="true"
              ></FieldCreator>
            </div>
          </div>
        </BlockContent>

        <BlockContent
          v-if="isPending && (!contract.esign || contractContainsActorType('MANAGER'))"
          e2e-block="contractDocumentSignAt"
        >
          <div class="row">
            <div class="col-md-6">
              <FieldCreator
                :form-data="formData"
                name="signedAt"
                form-ref="formcontract"
                domain="contract"
                label-left
              />
            </div>
            <div class="col-md-2 mt-2" v-if="!user.isReadOnly && loadField && def['signedAt'] && !isDelivery">
              <ButtonAll
                margin="no"
                :type="contract.signedAt ? 'success' : 'danger'"
                icon="calendar"
                @click="patchSignedAt"
                :loading="sending"
                e2e-button="contractDocumentSignedAt"
              />
            </div>
          </div>
        </BlockContent>

        <BlockContent
          v-if="def?.rib?.options?.display"
          title-t="simulation.fields.rib.placeholder"
          :title-level="4"
          e2e-block="contractDocumentRib"
        >
          <div class="card-body">
            <FieldCreator :form-data="formData" name="rib" form-ref="formcontract" domain="contract" refresh reload />
          </div>
        </BlockContent>

        <BlockContent title-t="contract.document.title" e2e-block="contractDocumentList">
          <DocumentList
            ref="documentList"
            :read-only="user.isReadOnly"
            id="contract-document"
            context="Contract"
            :context-id="Number(contractId)"
            @document-loaded="documentLoaded"
          />
        </BlockContent>

        <BlockContent
          title-t="contract.document.sign"
          v-if="!user.isReadOnly && isContractValidate && !hasRole(Role.ENHANCED_SIGN)"
          e2e-block="contractDocumentSign"
        >
          <div class="bg-pale-secondary b-1 border-light p-20">
            <div v-if="ribok">
              <div v-if="hasRole('ROLE_CONTRACT_SIGN_CHECK') && contract.status === 'VALIDATED'">
                <p class="text-center"><b></b></p>
                <div class="col-12 text-center">
                  <ButtonAll
                    margin="no"
                    :href="getSignEdition"
                    type="outline_success"
                    size="large"
                    label="contract.document.editDemand"
                    name="updateSign"
                  />
                </div>
              </div>
              <div
                v-if="['STUDIED', 'PENDING'].includes(contract.status) && Object.entries(configEquipment).length > 0"
              >
                <div v-if="contract.esign">
                  <template v-if="!!signError">
                    <ContractSignatureAlert :error="signError">
                      <div v-if="signError.code === 'esign.cancelled'" class="mt-2">
                        <strong>{{ $t('esign.renewAfterCancellation') }}</strong>
                      </div>
                      <div v-if="signComments?.length > 0" class="mt-2 col-md-4 mx-auto">
                        <strong>{{ $t('esign.signComments') }}</strong>
                        <ul class="list-unstyled mb-0 ps-3">
                          <li v-for="(comment, index) in signComments" :key="`${comment}-${index}`" class="mb-1">
                            <strong>"{{ comment }}"</strong>
                          </li>
                        </ul>
                      </div>
                    </ContractSignatureAlert>
                  </template>
                  <template v-else>
                    <p class="text-center font-weight-bold">{{ $t('contract.document.signDelivery') }}</p>
                    <p class="text-center font-weight-bold">{{ $t('contract.document.autoSign') }}</p>
                    <p v-if="hasValidator" :class="getValidationClasses">{{ getValidationInfoLabel }}</p>

                    <div v-if="!isDocumentCanceled" class="col-12 text-center">
                      <ButtonAll
                        margin="no"
                        type="outline_success"
                        size="large"
                        :label="getSignLabel"
                        @click="sendSign"
                        e2e-button="sendSign"
                      />
                      <ButtonAll
                        margin="no"
                        type="outline_info"
                        size="large"
                        label="contract.document.sendMail"
                        class="ml-4"
                        @click="sendEmail"
                        :loading="loading"
                        e2e-button="sendSignEmail"
                      />
                    </div>
                  </template>
                </div>
                <div v-else>
                  <p class="text-center font-weight-bold" v-html="$t('contract.document.manualSign')"></p>
                  <div class="col-12 text-center">
                    <ButtonAll
                      margin="no"
                      :href="getSignUrl"
                      type="danger"
                      size="large"
                      target="_blank"
                      label="contract.document.signNow"
                      e2e-button="goToSign"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div v-else>
              {{ $t('contract.document.rib_mandatory') }}
            </div>
          </div>
        </BlockContent>

        <ContractSignatureBlock
          v-if="canBeSignedByLessor"
          :document-sign="lessorContractDocumentSign"
          :e-sign="contract.esign"
          @email="sendContractSigningEmailToLessor"
          @download="download"
        />

        <div
          v-if="!user.isReadOnly && contract.status === 'PENDING' && !hasRole(Role.ENHANCED_SIGN)"
          class="col-12 text-center"
        >
          <ButtonAll
            margin="no"
            type="danger"
            size="large"
            label="contract.cancelSign"
            @click="onCancelSignature"
            :loading="canceling"
            e2e-button="cancelSign"
          />
          <ModalConfirmation
            id="cancelSignatureConfirm"
            ref="cancelSignatureConfirm"
            title="contract.information.actionConfirmed.cancelSign"
            message="contract.cancelSign"
            @confirm="onCancelSignatureConfirmation"
          />
        </div>

        <div class="col-12 text-center mt-2">
          <ButtonAll
            margin="no"
            type="validation"
            size="large"
            label="contract.document.watch"
            @click="download"
            e2e-button="contractDownload"
          />
        </div>
      </div>

      <ContractSignatureTab v-else-if="tab?.key === 'signatureFlows'" :contract="contract" />

      <WalletContractInvoiceList
        v-else-if="tab?.key === 'supplierInvoices'"
        :contract-id="Number(contractId)"
        supplier-invoices
      />
    </div>
    <div v-else>
      <div class="row mt-5">
        <div class="col flex-fill">
          <div class="row py-3 mb-3 mx-3 loading"></div>
        </div>
      </div>
    </div>
  </div>
</template>
